import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  tableCellClasses,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Height,
  Person as PersonIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchAccountLedgers,
  fetchJournalEntriesPartyLedgerReport,
  downloadJournalEntriesPartyLedgerReportExcel
} from '../../store/reportStore/reportThunk';
import { clearPartyLedgerReportData } from '../../store/reportStore/reportslice';
import ReusableReportHeader from '../../components/Reports/ReusableReportHeader';
import Filters from '../../components/Filters/Filters';
import { ApiStatus } from '../../utils/api.types';
import { VocuherCategory, Utils } from '../../utils/common-utils';
import { toast } from 'react-toastify';
import styled_ from "@emotion/styled";

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#f5f5f5',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    padding: '8px 12px',
    fontSize: '9pt',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: '6px 12px',
    border: '0.75px solid #e0e0e0',
  },
}));

const StyledTableRow = styled_(TableRow)({
  '&:nth-of-type(even)': {
    backgroundColor: '#fafafa',
  },
  '&:hover': {
    backgroundColor: '#f0f7ff',
  },
});

const JournalEntriesPartyLedgerReportPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { partyLedgerReportData, accountLedgers, status, errorMessage } = useAppSelector((state) => state.reports);
  const { selectedAccount } = useAppSelector((state) => state.auth);
  const accountId = selectedAccount?._id;

  const [filters, setFilters] = useState('');
  const [filterData, setFilterData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLedgers, setSelectedLedgers] = useState<any[]>([]);
  const [expandedParties, setExpandedParties] = useState<string[]>([]);
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  // Fetch ledgers list when filters change (to get ledgers for the selected date range)
  useEffect(() => {
    if (accountId && filters) {
      dispatch(fetchAccountLedgers({ accountId, params: filters }));
    }
  }, [accountId, filters, dispatch]);

  // Fetch report when filters change (but not when ledgers change - handled in handleLedgerSelectionChange)
  useEffect(() => {
    if (accountId && filters) {
      setIsLoading(true);

      // Reset selected ledgers when filters change
      setSelectedLedgers([]);

      dispatch(fetchJournalEntriesPartyLedgerReport({
        accountId,
        params: filters
      })).finally(() => {
        setIsLoading(false);
      });
    }
  }, [accountId, filters, dispatch]);

  // Clear data when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearPartyLedgerReportData());
    };
  }, [dispatch]);

  const filterOnChangeHandler = (data: any) => {
    setFilters(data);
  };

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data);
  };

  const handleLedgerSelectionChange = (event: any, value: any[]) => {
    setSelectedLedgers(value);
    // Automatically trigger report fetch when ledgers change
    if (accountId && filters) {
      setIsLoading(true);
      let queryParams = filters;

      // Only add ledger filter if ledgers are selected
      if (value.length > 0) {
        const ledgerIds = value.map(l => l._id).join(',');
        queryParams += `&ledgerIds=${ledgerIds}&showOnlySelected=true`;
      }
      // If no ledgers selected, queryParams will just be filters (without ledger parameters)

      dispatch(fetchJournalEntriesPartyLedgerReport({
        accountId,
        params: queryParams
      })).finally(() => {
        setIsLoading(false);
      });
    }
  };

  const handlePartyAccordionToggle = (partyId: string) => {
    setExpandedParties(prev =>
      prev.includes(partyId)
        ? [] // Close current accordion
        : [partyId] // Open only this accordion, close others
    );
  };

  const handleExpandCollapseToggle = () => {
    if (isAllExpanded) {
      // Collapse all
      setExpandedParties([]);
      setIsAllExpanded(false);
    } else {
      // Expand all
      if (partyLedgerReportData && partyLedgerReportData.parties) {
        setExpandedParties(partyLedgerReportData.parties.map((p: any) => p.partyId));
      }
      setIsAllExpanded(true);
    }
  };

  const handleDownloadExcel = () => {
    if (!accountId || !filters) {
      toast.error("Please select date range to download Excel");
      return;
    }

    // Build query params
    let queryParams = filters + '&isDownloadExcel=true';
    if (selectedLedgers.length > 0) {
      const ledgerIds = selectedLedgers.map(l => l._id).join(',');
      queryParams += `&ledgerIds=${ledgerIds}&showOnlySelected=true`;
    }

    dispatch(downloadJournalEntriesPartyLedgerReportExcel({
      accountId,
      params: queryParams
    })).unwrap().then((data) => {
      if (data) {
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        a.download = `Party_Ledger_Report_${timestamp}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Excel download completed");
      }
    }).catch((error: any) => {
      toast.error(error.message || "Failed to download Excel");
      console.error("Error downloading Excel:", error);
    });
  };

  const parties = partyLedgerReportData?.parties || [];
  const summary = partyLedgerReportData?.summary || {};

  return (
    <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2 }} className={'c-card2'}>
      {/* Scrollable content area */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}>
        {/* Report Header */}
        <ReusableReportHeader
          reportType="JOURNAL_ENTRIES_PARTY_LEDGER"
          title="Journal Entries - Party Wise Ledger Report"
          subtitle={filterData?.periodString || ''}
          recordCount={summary.totalParties || 0}
          isLoading={isLoading || status === ApiStatus.LOADING}
          onDownloadExcel={handleDownloadExcel}
          showPrint={false}
          showShare={false}
        />

        {/* Filters Row: Party/Date Filter + Ledger Filter + Expand/Collapse */}
        <Box px={2} py={1.5} pb={3}>
          <Grid container alignItems="center">
            {/* Party and Date Filters */}
            <Grid item xs={12} md={6}>
              <Filters
                type={VocuherCategory.SALE}
                filterHandler={filterOnChangeHandler}
                selectedFilters={filterdOnChangeHandler}
                hideFilter={true}
                hidePartySearch={false}
              />
            </Grid>

            {/* Ledger Filter */}
            <Grid item xs={12} md={4.5}>
              <Box>
                <Autocomplete
                  multiple
                  id="ledger-select"
                  options={accountLedgers || []}
                  getOptionLabel={(option: any) => `${option.name} (${option.code})`}
                  value={selectedLedgers}
                  onChange={handleLedgerSelectionChange}
                  disableCloseOnSelect
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className='c-text-field-3'
                      variant="standard"
                      size="small"
                      placeholder="Select ledgers to filter..."
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                      sx={{
                        '& .MuiInputBase-root': {
                          fontSize: '0.875rem',
                          paddingTop: '2px !important',
                          display: 'flex',
                          alignItems: 'center'
                        },
                        '& .MuiInputBase-input': {
                          paddingTop: '0 !important',
                          paddingBottom: '0 !important'
                        },
                        '& .MuiAutocomplete-endAdornment': {
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option: any) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.code} • {option.groupCategory}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderTags={() => null}
                  sx={{ width: '100%' }}
                />

                {/* Selected Ledgers Display - Below the search bar */}
                {selectedLedgers.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {selectedLedgers.map((ledger: any) => (
                      <Chip
                        key={ledger._id}
                        label={`${ledger.name} (${ledger.code})`}
                        size="small"
                        onDelete={() => {
                          const newSelectedLedgers = selectedLedgers.filter((l: any) => l._id !== ledger._id);
                          handleLedgerSelectionChange(null, newSelectedLedgers);
                        }}
                        sx={{ fontSize: '0.7rem', height: '22px' }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Expand/Collapse Toggle */}
            <Grid item xs={12} md={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isAllExpanded}
                      onChange={handleExpandCollapseToggle}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption" fontWeight={500}>
                      {isAllExpanded ? 'Collapse All' : 'Expand All'}
                    </Typography>
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Summary Cards Row */}
        {/* {parties.length > 0 && (
          <Box px={2} pb={2}>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-start' }}>
              <Paper elevation={0} sx={{ py: 1, px: 1.5, border: '1px solid #e3f2fd', backgroundColor: '#f0f7ff', borderRadius: 1.5, textAlign: 'center', minWidth: '110px' }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>Total Parties</Typography>
                <Typography variant="h6" fontWeight={600} color="primary" sx={{ fontSize: '1.25rem' }}>{summary.totalParties || 0}</Typography>
              </Paper>
              <Paper elevation={0} sx={{ py: 1, px: 1.5, border: '1px solid #e8f5e9', backgroundColor: '#f1f8f4', borderRadius: 1.5, textAlign: 'center', minWidth: '110px' }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>Total Transactions</Typography>
                <Typography variant="h6" fontWeight={600} color="success.main" sx={{ fontSize: '1.25rem' }}>{summary.totalTransactions || 0}</Typography>
              </Paper>
            </Box>
          </Box>
        )} */}

        {/* Report Content */}
        <Box>
          {status === ApiStatus.LOADING || isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
              <Typography variant="body1" color="text.secondary">
                Loading report data...
              </Typography>
            </Box>
          ) : errorMessage ? (
            <Alert severity="error" sx={{ mb: 2, mx: 2 }}>
              Error: {errorMessage}
            </Alert>
          ) : !parties || parties.length === 0 ? (
            <Alert severity="info" sx={{ mx: 2 }}>
              No journal entries found. Please adjust your filters or date range.
            </Alert>
          ) : (
            <Box>
              {parties.map((party: any) => (
                <Accordion
                  key={party.partyId}
                  expanded={expandedParties.includes(party.partyId)}
                  onChange={() => handlePartyAccordionToggle(party.partyId)}
                  disableGutters
                  sx={{
                    border: "1px solid #000",
                    borderLeft: 0,
                    borderRight: 0,
                    borderRadius: 0,
                    "&:before": { display: "none" },
                    "&:not(:last-child)": { borderBottom: 0 }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: '#fafafa',
                      '&:hover': { backgroundColor: '#f0f0f0' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                      <PersonIcon sx={{ mr: 2, color: party.partyType === 'Customer' ? '#1976d2' : '#f57c00' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{party.partyName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {party.partyType} • {party.partyGSTIN || 'No GSTIN'} • {party.transactionCount} transactions
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Total Debit</Typography>
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            {Utils.formatNumber(party.totalDebit, false, true)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Total Credit</Typography>
                          <Typography variant="body2" fontWeight={600} color="error.main">
                            {Utils.formatNumber(party.totalCredit, false, true)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Balance</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {Utils.formatNumber(party.balance, false, true)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 0 }}>
                    {party.ledgers && party.ledgers.length > 0 ? (
                      party.ledgers.map((ledger: any) => (
                        <Box key={ledger.ledgerId} sx={{ mb: 2, mx: 2, mt: 2, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                          {/* Ledger Header */}
                          <Box sx={{ p: 1.5, backgroundColor: '#f0f7ff' }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" fontWeight={600} >
                                  {ledger.ledgerName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Code: {ledger.ledgerCode} • Group: {ledger.groupCategory || ledger.primaryGroup}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                  <Box textAlign="right">
                                    <Typography variant="caption" color="text.secondary">Debit</Typography>
                                    <Typography variant="body2" fontWeight={600} color="success.main">
                                      {Utils.formatNumber(ledger.totalDebit, false, true)}
                                    </Typography>
                                  </Box>
                                  <Box textAlign="right">
                                    <Typography variant="caption" color="text.secondary">Credit</Typography>
                                    <Typography variant="body2" fontWeight={600} color="error.main">
                                      {Utils.formatNumber(ledger.totalCredit, false, true)}
                                    </Typography>
                                  </Box>
                                  <Box textAlign="right">
                                    <Typography variant="caption" color="text.secondary">Balance</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {Utils.formatNumber(ledger.balance, false, true)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>

                          {/* Ledger Transactions Table */}
                          {ledger.transactions && ledger.transactions.length > 0 && (
                            <Table size="small" sx={{ borderTop: '1px solid #e0e0e0' }}>
                              <TableHead>
                                <TableRow>
                                  <StyledTableCell>Date</StyledTableCell>
                                  <StyledTableCell>Voucher Number</StyledTableCell>
                                  <StyledTableCell>Voucher Type</StyledTableCell>
                                  <StyledTableCell align="right">Debit</StyledTableCell>
                                  <StyledTableCell align="right">Credit</StyledTableCell>
                                  <StyledTableCell>Narration</StyledTableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {ledger.transactions.map((txn: any, idx: number) => (
                                  <StyledTableRow key={idx}>
                                    <StyledTableCell>{txn.date}</StyledTableCell>
                                    <StyledTableCell>{txn.voucherNumber}</StyledTableCell>
                                    <StyledTableCell>{txn.voucherType}</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ color: txn.debit !== '0.00' ? 'success.main' : 'text.disabled' }}>
                                      {txn.debit !== '0.00' ? Utils.formatNumber(txn.debit, false, true) : '-'}
                                    </StyledTableCell>
                                    <StyledTableCell align="right" sx={{ color: txn.credit !== '0.00' ? 'error.main' : 'text.disabled' }}>
                                      {txn.credit !== '0.00' ? Utils.formatNumber(txn.credit, false, true) : '-'}
                                    </StyledTableCell>
                                    <StyledTableCell>{txn.narration || '-'}</StyledTableCell>
                                  </StyledTableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}

                        </Box>
                      ))
                    ) : (
                      <Box p={2}>
                        <Typography variant="body2" color="text.secondary">
                          No ledger entries found for this party.
                        </Typography>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default JournalEntriesPartyLedgerReportPage;
